import {
  RedirectLoginOptions,
  useAuth0,
  WithAuthenticationRequiredOptions,
  withAuthenticationRequired,
} from '@auth0/auth0-react';
import { IDENTIFIER_FIRST_LOGIN, URL_QUERY_PARAMETERS } from 'consts/consts';
import React, { useContext } from 'react';
import { Redirect } from 'react-router-dom';

import { KeycloakAuthContext } from './KeycloakAuthContext';
import { getActiveAuthProvider, isKeycloakEnabled } from './keycloakClient';

// Shared shape so useAuthProvider()'s ternary below (Auth0's real
// withAuthenticationRequired vs. our identifierFirstWithAuthenticationRequired)
// resolves to one coherent callable type instead of TypeScript trying to
// reconcile two structurally different function types at the call site.
type WithAuthenticationRequired = <P extends object>(
  Component: React.ComponentType<P>,
  options?: WithAuthenticationRequiredOptions
) => React.ComponentType<P>;

/**
 * Keycloak has no library-provided withAuthenticationRequired, so this
 * replicates @auth0/auth0-react's one closely enough for ProtectedRoute.tsx
 * to call it identically regardless of which provider is active: same
 * (Component, options) signature (options is actually whatever
 * redirectLoginOptionsGenerator() produced - a RedirectLoginOptions, not a
 * WithAuthenticationRequiredOptions; ProtectedRoute.tsx has always called it
 * this way, even for Auth0's own withAuthenticationRequired, which just
 * reads its expected fields as undefined and falls back to its defaults),
 * same "render the Component the moment isAuthenticated is true, ignoring
 * isLoading" rule (see useAuthProvider() below for why that rule matters).
 *
 * Unlike Auth0's version this never calls loginWithRedirect() itself -
 * picking a provider is the backend's call via /api/auth/resolve-provider,
 * not whichever one happened to be active before. Every unauthenticated
 * visit while Keycloak is enabled goes through the identifier-first login
 * page instead, so the user (re-)picks by email - see
 * views/Login/IdentifierFirstLogin.tsx.
 */
const identifierFirstWithAuthenticationRequired: WithAuthenticationRequired = (
  Component,
  options
) => {
  return function WithIdentifierFirstAuthenticationRequired(props) {
    const { isAuthenticated, isLoading } = useAuthProvider();

    if (isAuthenticated) {
      return <Component {...props} />;
    }

    if (isLoading) {
      return null;
    }

    // See this function's doc comment - options is really a
    // RedirectLoginOptions at the one call site (ProtectedRoute.tsx), which
    // is where continueUrl actually lives (as appState.returnTo), not on
    // WithAuthenticationRequiredOptions's own (unused here) returnTo field.
    const continueUrl =
      (options as RedirectLoginOptions | undefined)?.appState?.returnTo ??
      window.location.pathname;
    return (
      <Redirect
        to={
          IDENTIFIER_FIRST_LOGIN.path +
          '?' +
          URL_QUERY_PARAMETERS.CONTINUE_URL +
          '=' +
          encodeURIComponent(continueUrl)
        }
      />
    );
  };
};

/**
 * Provider-agnostic replacement for @auth0/auth0-react's useAuth0(). Returns
 * the Auth0 SDK's own context value for the (default, common) Auth0 case,
 * or the Keycloak-backed equivalent (see KeycloakAuthContext.tsx) when the
 * user's last login was via Keycloak - see keycloakClient.ts and
 * views/Login/IdentifierFirstLogin.tsx for where that choice is made.
 *
 * Every call site that previously called useAuth0() directly should call
 * this instead - the returned shape (isAuthenticated, isLoading, user,
 * loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims,
 * withAuthenticationRequired) matches what those call sites already
 * destructure.
 *
 * isKeycloakEnabled() is checked here too (not just at the login gates) so
 * that if the Keycloak kill switch flips off mid-session for a user who's
 * already Keycloak-authenticated (stale ACTIVE_AUTH_PROVIDER_KEY), the app
 * falls back to Auth0's state rather than keep reading Keycloak's.
 *
 * withAuthenticationRequired is switched on isKeycloakEnabled() alone, not
 * on the active-provider check above: an unauthenticated, never-logged-in
 * visitor has no "active provider" yet, so whether they should land on the
 * identifier-first page depends only on whether Keycloak is enabled at all
 * for this environment - while Keycloak is fully disabled (every deployed
 * environment today), this is @auth0/auth0-react's own
 * withAuthenticationRequired, completely unchanged, so that path's
 * behavior can't drift from upstream.
 */
export const useAuthProvider = () => {
  const auth0 = useAuth0();
  const keycloakAuth = useContext(KeycloakAuthContext);

  return {
    ...(isKeycloakEnabled() && getActiveAuthProvider() === 'keycloak'
      ? keycloakAuth
      : auth0),
    withAuthenticationRequired: (isKeycloakEnabled()
      ? identifierFirstWithAuthenticationRequired
      : withAuthenticationRequired) as WithAuthenticationRequired,
  };
};
